import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import ShipeeziGrid, {
  Column,
  GeneralActions,
  ColumnFilter,
  SearchSuggest,
  Category,
} from "Components/AppGrid/shipeezi-new-grid";
import _ from "lodash";
import { useTranslation } from "react-i18next";

import { useAppState } from "../../../context/StateContext";
import { Booking } from "../../../models/booking";
import { CartonComment, CartonProductView, CartonType, CartonView } from "../../../models/carton";
import { Product } from "../../../models/product";
import { PurchaseOrder, PurchaseOrderProduct } from "../../../models/purchase.order";
import { SearchAttribute } from "../../../models/search.attribute";
import { Filters } from "../../../models/shipeezi.table";
import { ShipmentDocument } from "../../../models/shipment.document";
import { ValueOf, ViewOnly } from "../../../types";
import { BookingStatus, CartonStatus, ShipmentType } from "../../../util/enums";
import { filterSearch } from "../../../util/filterSearch";
import { onSearch } from "../../../util/onSearch";
import { paginate } from "../../../util/paginate";
import { NotificationToast } from "../Toast/NotificationToast";
import { UploadCSVFileHandles } from "../UploadCSVFile";
import AddContainerButton from "./components/AddContainerButton";
import ContainerModal, { ContainerModalHandles } from "./components/AddContainerModal";
import AfterRow from "./components/AfterRow";
import CloseCartonButton from "./components/CloseCartonButton";
import ConfirmReceiveSSCCModal, {
  ConfirmReceiveSSCCHandles,
} from "./components/ConfirmReceiveSSCC";
import CreateCommentModal, { CommentModalHandles } from "./components/CreateCommentModal";
import Button, { Color } from "../../Form/Button";
import { DefaultTooltip } from "../Tooltip";
import DeleteCartonButton from "./components/DeleteCartonButton";
import { CartonSSCCEmptySection } from "./components/EmptySection";
import HeaderQuantities from "./components/HeaderQuantities";
import ModalAddProduct from "./components/ModalAddProduct";
import CloseSSCCModal, { CloseSSCCConfirmationModalHandles } from "./components/ModalCloseSSCC";
import ModalEditProduct, { EditProductModalHandles } from "./components/ModalEditProduct";
import PrintLabelButton from "./components/PrintLabelButton";
import ReceiveAllSSCCButton from "./components/ReceiveAllSSCCButton";
import RowSSCC from "./components/RowSSCC";
import CommentsHistoryModal, {
  CommentsHistoryModalHandles,
} from "./components/RowSSCC/CommentsHistoryModal";
import UploadCSVButton from "./components/UploadCSVButton";
import UploadCSVCartons from "./components/UploadCSVCartons";
import UploadCSVReceiveButton from "./components/UploadCSVReceiveButton";
import UploadCSVReceivedSSCC from "./components/UploadCSVReceivedSSCC";
import SSCCPrerequisitesTooltip, {
  ssccPrerequisitesTooltipSlotProps,
} from "./components/SSCCPrerequisitesTooltip";
import useAction from "./hooks/useAction";
import { useSSCC } from "./hooks/useSSCC";
import useStyles from "./styles";
import { CartonSSCCFrom, SearchColumOption, SubTitleProps } from "./util";

// TODO Felipe: this value need be received from settings
export const LIMIT_PRODUCTS_INSIDE_CARTONS = 4;

interface CartonSSCCProps {
  cartons: CartonView[];
  products?: PurchaseOrderProduct[];
  updateCartons?: (cartons: CartonView[]) => void;
  purchaseOrder?: PurchaseOrder;
  loadingCartons?: boolean;
  onDeleteSSCC?: (sscc?: string[]) => void;
  subtitle?: SubTitleProps;
  viewOnly: ViewOnly;
  viewSupplier?: boolean;
  calledFrom: CartonSSCCFrom;
  booking?: Booking;
  resources?: {
    purchaseOrders: PurchaseOrder[];
    products?: PurchaseOrderProduct[];
    resourceCompanyId?: number;
  };
  loadingProducts?: boolean;
  isLengthMandatory?: boolean;
  statusBooking?: string;
  onUploadCsvS3?: (csv: ShipmentDocument) => void;
  fromMaintenance?: boolean;
  openedFromView?: boolean;
  showOriginCriterion?: boolean;
  ssccPrerequisiteMessages?: string[];
}

const CartonSSCC: React.FC<CartonSSCCProps> = ({
  cartons,
  updateCartons,
  onDeleteSSCC,
  purchaseOrder,
  subtitle,
  viewOnly: viewMode,
  viewSupplier,
  calledFrom,
  booking,
  resources,
  loadingProducts,
  isLengthMandatory,
  onUploadCsvS3,
  statusBooking = BookingStatus.PENDING,
  openedFromView,
  showOriginCriterion,
  ssccPrerequisiteMessages = [],
}) => {
  const isSSCCActionBlocked = ssccPrerequisiteMessages.length > 0;
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { isSupplierPortal: isSPPVersion, currentUser, isFMVersion, isSPVersion } = useAppState();
  const { fields: viewOnly } = viewMode;

  const isBrazilDestinationPort =
    booking?.portOfDestination?.country?.code === "BR" ||
    !!booking?.portOfDestination?.siteCode?.startsWith("BR");

  const { useGenerateSSCC } = useAction();
  const {
    handlePrintLabel,
    selectedId,
    generatingLabelReport,
    setSelectedId,
    handleGenerateLabelReport,
  } = useSSCC({
    booking,
    cartons,
    purchaseOrder,
  });

  const editProductRef = useRef<EditProductModalHandles>(null);
  const closeSSCCModalRef = useRef<CloseSSCCConfirmationModalHandles>(null);
  const closeSSCCsModalRef = useRef<CloseSSCCConfirmationModalHandles>(null);
  const closeAllSSCCModalRef = useRef<CloseSSCCConfirmationModalHandles>(null);
  const uploadCSVRef = useRef<UploadCSVFileHandles>(null);
  const uploadCSVReceiveRef = useRef<UploadCSVFileHandles>(null);
  const confirmReceiveRef = useRef<ConfirmReceiveSSCCHandles>(null);
  const containerRef = useRef<ContainerModalHandles>(null);
  const commentRef = useRef<CommentModalHandles>(null);
  const commentsHistoryRef = useRef<CommentsHistoryModalHandles>(null);

  const calledFromBooking = useMemo(() => calledFrom === CartonSSCCFrom.BOOKING, [calledFrom]);
  const calledFromShipment = useMemo(() => calledFrom === CartonSSCCFrom.SHIPMENT, [calledFrom]);
  const bookingPending = useMemo(() => statusBooking === BookingStatus.PENDING, [statusBooking]);

  const isThereCartonsOpen = useMemo(() => !!cartons.find((carton) => !carton.isClosed), [cartons]);

  const [collapseds, setCollapseds] = useState<string[]>([]);

  const [searchAttributes, setSearchAttributes] = useState<SearchAttribute[]>([]);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [selecteds, setSelecteds] = useState<string[]>([]);
  const [selectedCarton, setSelectedCarton] = useState<string>();
  const [orderToFilter, setOrderToFilter] = useState<string>();

  const [searchInput, setSearchInput] = useState<string>("");
  const [searchOptions, setSearchOptions] = useState<ColumnFilter[]>([]);

  const [openAddProduct, setOpenAddProduct] = useState<boolean>(false);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [cartonsData, setCartonsData] = useState<CartonView[]>([]);

  const openCloseSSCCModal = useCallback(() => closeSSCCModalRef?.current?.open(), []);
  const closeCloseSSCCModal = useCallback(() => closeSSCCModalRef?.current?.close(), []);

  const closeCloseAllSSCCModal = useCallback(() => closeAllSSCCModalRef?.current?.close(), []);
  const closeCloseSSCCsModal = useCallback(() => closeSSCCsModalRef?.current?.close(), []);
  const isThereCartonWithNoProducts = useMemo(() => {
    if (!selecteds || !selecteds?.length) return false;

    for (const selected of selecteds) {
      const carton = cartons?.find((carton) => carton?.sscc === selected);

      if (carton) {
        if (!carton?.products || !carton?.products?.length) return true;
      }
    }

    return false;
  }, [cartons, selecteds]);
  const isThereCartonsWithNoProducts = useMemo(() => {
    if (!cartons || !cartons.length) return false;

    for (const carton of cartons) {
      if (carton) {
        if (!carton?.products || !carton?.products?.length) return true;
      }
    }

    return false;
  }, [cartons]);
  const isThereCartonClosed = useMemo(() => {
    if (!selecteds || !selecteds?.length) return false;

    for (const selected of selecteds) {
      const carton = cartons?.find((carton) => carton?.sscc === selected);

      if (carton) {
        if (carton?.isClosed) return true;
      }
    }

    return false;
  }, [cartons, selecteds]);

  useEffect(() => {
    let data = cartons;

    if (searchAttributes.length > 0) {
      data = filterSearch(cartons, searchAttributes);
      const isOutOfRange = data.length < (page - 1) * pageSize;

      if (isOutOfRange) setPage(1);
      setTotalItems(data.length);
    } else {
      setTotalItems(cartons.length);
    }
    setCartonsData(paginate(data, page > 0 ? page - 1 : 0, pageSize));
  }, [cartons, page, pageSize, searchAttributes]);

  useEffect(() => {
    if (!cartons || !cartons.length) setSelecteds([]);
  }, [cartons]);

  const toggleCollapse = useCallback((sscc?: string) => {
    if (sscc) {
      setCollapseds((prev) => {
        if (prev.find((item) => item === sscc)) {
          return prev.filter((item) => item !== sscc);
        }

        return [...prev, sscc];
      });
    }
  }, []);

  const { mutate: generateSSCC, isLoading: isGeneratingSSCC } = useGenerateSSCC();

  const onAddProductIntoCarton = (cartonSSCC?: string, orderToFilter?: string) => {
    const carton = cartons.find((carton) => carton.sscc === cartonSSCC);

    setOrderToFilter(orderToFilter);
    setSelectedCarton(carton?.sscc);
    setOpenAddProduct(true);
  };

  const onCloseCarton = (cartonSSCC?: string) => {
    if (!cartonSSCC) return;
    setSelectedId(cartonSSCC);
    openCloseSSCCModal();
  };

  const onConfirmActionInAllCartons = (property: keyof CartonView, value?: ValueOf<CartonView>) => {
    if (!updateCartons || !property) return;

    const cartonsToUpdate = cartons.map((carton) => ({
      ...carton,
      [property]: value,
    }));

    updateCartons(cartonsToUpdate);
  };

  const onCloseAllSSCCs = (closeAll?: boolean) => {
    if (!updateCartons) return;

    const cloned = _.cloneDeep(cartons);

    if (closeAll) {
      cloned.forEach((carton) => {
        carton.isClosed = true;
      });
    } else {
      for (const selected of selecteds) {
        const cartonIndex = cloned.findIndex((carton) => carton.sscc === selected);

        if (cartonIndex < 0 || !cloned[cartonIndex]) return;
        const carton = cloned[cartonIndex];

        cloned.splice(Number(cartonIndex), 1, { ...carton, isClosed: true } as CartonView);
      }
    }

    updateCartons(cloned);
  };

  const onConfirmActionInCarton = (
    cartonSSCC?: string,
    property?: keyof CartonView,
    value?: ValueOf<CartonView>
  ) => {
    if (!updateCartons || !cartonSSCC || !property) return;

    const cloned = _.cloneDeep(cartons);
    const cartonIndex = cloned.findIndex((carton) => carton.sscc === cartonSSCC);

    if (cartonIndex < 0 || !cloned[cartonIndex]) return;
    const carton = cloned[cartonIndex];

    cloned.splice(Number(cartonIndex), 1, { ...carton, [property]: value } as CartonView);
    updateCartons(cloned);
  };
  const deleteSSCC = (sscc?: string) => {
    if (onDeleteSSCC) onDeleteSSCC(sscc ? [sscc] : undefined);
  };

  const onCreateComment = (cartonSscc: string, message: string) => {
    if (!cartonSscc || !message) return;

    const currentCarton = cartons.find((carton) => carton.sscc === cartonSscc);

    onConfirmActionInCarton(cartonSscc, "comments", [
      ...(currentCarton?.comments || []),
      { userId: currentUser?.id, message },
    ]);
  };

  const onDeleteComment = (cartonSscc: string, comments: CartonComment[]) => {
    if (!cartonSscc) return;
    onConfirmActionInCarton(cartonSscc, "comments", comments);
  };

  const columns: Column[] = [
    {
      name: "sscc",
      title: translate("sscc"),
      formatComponent: (value?: string, row?: CartonView) => (
        <RowSSCC
          collapseds={collapseds}
          value={value}
          row={row}
          calledFromBooking={calledFromBooking}
          viewOnly={viewOnly}
          viewSupplier={viewSupplier}
          loadingProducts={loadingProducts}
          loadingPrintingLabel={generatingLabelReport && value === selectedId}
          booking={booking}
          toggleCollapse={toggleCollapse}
          addProductIntoCarton={onAddProductIntoCarton}
          handlePrintLabel={booking?.id ? handlePrintLabel : undefined}
          confirmReceiveRef={confirmReceiveRef}
          closeCarton={onCloseCarton}
          deleteCarton={deleteSSCC}
          containerModalRef={containerRef}
          calledFromShipment={calledFromShipment}
          commentModalRef={commentRef}
          commentsHistoryRef={commentsHistoryRef}
        />
      ),
    },
  ];

  const onChangeSearchOptions = (options: ColumnFilter[]) => {
    setSearchOptions(options);
    const attributes = onSearch(options);

    setSearchAttributes(attributes);
  };

  const renderSearchSuggest = () => {
    return (
      <SearchSuggest
        columns={[
          { name: SearchColumOption.ORDER, title: `${translate("order")} #` },
          { name: SearchColumOption.SSCC, title: translate("sscc") },
          { name: SearchColumOption.SKU, title: `${translate("sku")} #` },
          { name: SearchColumOption.DESCRIPTION, title: translate("productName") },
          { name: SearchColumOption.COLOR, title: translate("colour") },
        ]}
        columnsSelected={searchOptions}
        onChangeColumnsSelected={onChangeSearchOptions}
        inputValue={searchInput}
        onChangeInput={setSearchInput}
        placeholder="Search"
      />
    );
  };

  const onSubmitEditProductFromCarton = (
    sscc: string,
    productIndex: number,
    product: CartonProductView
  ) => {
    if (!updateCartons) return;

    const cloned = _.cloneDeep(cartons);

    const cartonIndex = cloned.findIndex((carton) => carton.sscc === sscc);

    if (cartonIndex === -1) return;

    const carton = cloned[cartonIndex];

    if (!carton || !carton.products || !carton.products.length) return;

    const { products } = carton;

    products[productIndex] = product;

    updateCartons(cloned);
  };

  const handleUpdateCartons = (ssccList: string[]) => {
    if (!updateCartons || !ssccList[0].length) return;

    const newCarton = {
      sscc: ssccList[0],
      grossWeight: 0,
      type: CartonType.SOLID,
      products: [],
      isClosed: false,
    } as CartonView;

    const cartonsUpdated = [newCarton, ...cartons];

    updateCartons(cartonsUpdated);
    NotificationToast.success(translate("newSSCCwasCreated"), {
      id: "create-carton-success",
    });
  };

  const AddContainersInCartons = (container?: string, ssccs?: string[]) => {
    const cloned = _.cloneDeep(cartons);

    if (!updateCartons || !ssccs?.length) return;

    for (const sscc of ssccs) {
      const cartonIndex = cloned.findIndex((carton) => carton.sscc === sscc);

      if (cartonIndex === -1) return;

      const carton = cloned[cartonIndex];

      if (!carton) return;

      carton.container = container;
    }

    updateCartons(cloned);
  };

  const ssccPrerequisitesTooltip = useMemo(() => {
    if (!isSSCCActionBlocked) return null;

    return <SSCCPrerequisitesTooltip translationKeys={ssccPrerequisiteMessages} />;
  }, [isSSCCActionBlocked, ssccPrerequisiteMessages]);

  const createSSCC = () => {
    if (isSSCCActionBlocked) return;

    let companyId = currentUser?.company?.supplierAssociated?.companyId;

    if (!isSPPVersion) {
      companyId = currentUser?.companyId;
    }

    if (!companyId) return;
    generateSSCC(
      { companyId },
      {
        onSuccess: (response) => {
          handleUpdateCartons(response.data);
        },
      }
    );
  };

  const onAddContainer = () => {
    if (!selecteds || !selecteds?.length) return;

    return containerRef?.current?.open(selecteds);
  };

  const allCartonsAlreadyReceived = useMemo(
    () => !cartons.find((carton) => !carton.isReceivedByCfs),
    [cartons]
  );

  const getSppRightButtons = () => {
    if (!openedFromView) {
      if (selecteds && selecteds?.length > 1) {
        return ["handleAddContainer", "handleCloseCarton", "handlePrintLabel", "handleDelete"];
      }

      return ["handleCloseAllCarton", "handleUploadCSV", "handleCreateSSCC"];
    }

    return ["handlePrintLabel"];
  };

  const getRightButtons = useCallback(() => {
    const rightButtonsSp = ["handlePrintLabel"];
    const rightButtonsFm = ["handleUploadCSVReceive", "handleReceiveAllSSCC"];
    const rightButtonsSpp = getSppRightButtons();

    if (calledFromShipment) return [...rightButtonsSpp];
    if (isFMVersion) return rightButtonsFm;
    if (isSPPVersion) return rightButtonsSpp;
    if (isSPVersion) return rightButtonsSp;

    return [];
  }, [selecteds]);

  const handleFilter = (filters: Filters) => {
    const attributes: SearchAttribute[] = [];

    if (filters) {
      Object.getOwnPropertyNames(filters).forEach((attribute) => {
        attributes.push({
          attribute,
          values: filters[attribute],
        });
      });
    }
    setSearchAttributes(attributes);
    setPage(0);
  };

  const filterCategories: Category[] = [
    {
      name: translate("cartonStatus"),
      column: isFMVersion ? "isReceivedByCfs" : "isClosed",
      options: isFMVersion
        ? [
            { value: "true", label: translate(CartonStatus.RECEIVED) },
            { value: "false", label: translate(CartonStatus.NOT_RECEIVED) },
          ]
        : [
            { value: "true", label: translate(CartonStatus.CLOSED) },
            { value: "false", label: translate(CartonStatus.OPEN) },
          ],
    },
  ];

  const showPrintLabel = useMemo(() => {
    if (calledFromShipment) {
      return !bookingPending;
    }

    return !isFMVersion && calledFromBooking && !bookingPending;
  }, [isFMVersion, calledFromBooking, bookingPending, calledFromShipment]);

  const generalActions: GeneralActions = {
    orderBy: {
      left: !openedFromView
        ? ["searchSuggest", "handleFilterBy", "renderQuantities"]
        : ["searchSuggest", "handleFilterBy"],
      right: !openedFromView ? getRightButtons() : [...getRightButtons(), "renderQuantities"],
    },
    searchSuggest: renderSearchSuggest(),
    handleFilterBy: {
      categories: filterCategories,
      onChange: handleFilter,
    },
    handleCreateSSCC: (() => {
      const showCreateSSCC =
        !!viewSupplier &&
        !viewOnly &&
        (calledFromBooking || calledFromShipment) &&
        !booking?.bookingId;

      if (!showCreateSSCC) return null;

      const tooltipTitle = ssccPrerequisitesTooltip ?? translate("createSSCC");

      return (
        <DefaultTooltip
          title={tooltipTitle}
          slotProps={isSSCCActionBlocked ? ssccPrerequisitesTooltipSlotProps : undefined}
        >
          <div>
            <Button
              loading={isGeneratingSSCC || loadingProducts}
              customColor={Color.SECONDARY}
              onClick={createSSCC}
              disabled={isSSCCActionBlocked}
            >
              <span className={classes.buttonText}>{translate("createSSCC")}</span>
            </Button>
          </div>
        </DefaultTooltip>
      );
    })(),
    renderQuantities: (
      <HeaderQuantities
        openedFromView={openedFromView}
        cartons={cartons}
        subtitle={subtitle}
        calledFromBooking={calledFromBooking}
        booking={booking}
        calledFromShipment={calledFromShipment}
      />
    ),
    handlePrintLabel: booking?.id ? (
      <PrintLabelButton
        cartons={cartons}
        show={showPrintLabel}
        loading={generatingLabelReport}
        selecteds={selecteds}
        isBookingCancelled={!!booking && booking?.statusShipment === BookingStatus.CANCELLED}
        handleGenerateLabelReport={handleGenerateLabelReport}
      />
    ) : null,

    handleUploadCSV: (
      <UploadCSVButton
        show={(calledFromBooking && isSPPVersion) || (calledFromShipment && !booking?.bookingId)}
        disabled={viewOnly || isSSCCActionBlocked}
        loading={loadingProducts}
        uploadCSVRef={uploadCSVRef}
        tooltip={ssccPrerequisitesTooltip ?? translate("uploadCsv")}
        tooltipSlotProps={isSSCCActionBlocked ? ssccPrerequisitesTooltipSlotProps : undefined}
      />
    ),
    handleUploadCSVReceive: (
      <UploadCSVReceiveButton
        show={isFMVersion && calledFromBooking && !viewOnly}
        loading={loadingProducts}
        allCartonsAlreadyReceived={allCartonsAlreadyReceived}
        uploadCSVReceiveRef={uploadCSVReceiveRef}
      />
    ),
    handleReceiveAllSSCC: (
      <ReceiveAllSSCCButton
        show={isFMVersion && calledFromBooking && !viewOnly}
        loading={loadingProducts}
        allCartonsAlreadyReceived={allCartonsAlreadyReceived}
        confirmReceiveRef={confirmReceiveRef}
      />
    ),
    handleAddContainer: (
      <AddContainerButton
        show={!!calledFromShipment && booking?.shipmentType === ShipmentType.SEA}
        loading={loadingProducts}
        disabled={!cartons || !!loadingProducts}
        onClick={onAddContainer}
      />
    ),
    handleCloseCarton: (
      <CloseCartonButton
        loading={loadingProducts}
        show={
          !!viewSupplier &&
          !viewOnly &&
          (calledFromBooking || calledFromShipment) &&
          isThereCartonsOpen
        }
        title={translate("closeSSCC")}
        isThereCartonWithNoProducts={isThereCartonWithNoProducts}
        closeRef={closeSSCCsModalRef}
        disabled={isThereCartonWithNoProducts || loadingProducts || !cartons || isThereCartonClosed}
      />
    ),
    handleCloseAllCarton: (
      <CloseCartonButton
        loading={loadingProducts}
        show={
          !!viewSupplier &&
          !viewOnly &&
          (calledFromBooking || calledFromShipment) &&
          isThereCartonsOpen
        }
        title={translate("closeAllSSCCs")}
        isThereCartonWithNoProducts={isThereCartonsWithNoProducts}
        closeRef={closeAllSSCCModalRef}
        disabled={isThereCartonsWithNoProducts || loadingProducts || !cartons}
      />
    ),
    handleDelete: (
      <DeleteCartonButton
        show={
          !!viewSupplier &&
          !viewOnly &&
          (calledFromBooking || calledFromShipment) &&
          !booking?.bookingId
        }
        deleteCarton={() => {
          if (onDeleteSSCC) onDeleteSSCC(selecteds);
        }}
      />
    ),
  };

  const renderModalAddProduct = () => {
    if (!openAddProduct) return;

    const addProducts = (addedProducts: Product[]) => {
      if (!updateCartons) return;

      const cartonsClone = _.cloneDeep(cartons);
      const cartonIndex = cartonsClone.findIndex((carton) => carton.sscc === selectedCarton);

      if (cartonIndex !== -1) {
        const productUpdated: CartonProductView[] = [
          ...(cartonsClone[cartonIndex]?.products || []),
          ...(addedProducts as CartonProductView[]),
        ];
        const cartonType = productUpdated?.length > 1 ? CartonType.MIXED : CartonType.SOLID;

        const [firstProduct] = productUpdated;
        const purchaseOrder = resources?.purchaseOrders.find(
          (purchaseOrder) => purchaseOrder?.reference === firstProduct?.poNumber
        );
        const purchaseOrderId = purchaseOrder?.id ?? 0;

        const cartonUpdated: CartonView = {
          ...cartonsClone[cartonIndex],
          type: cartonType,
          products: productUpdated,
          grossWeight: productUpdated.reduce((prev, curr) => prev + Number(curr.weight || 0), 0),
          purchaseOrderId,
        };

        cartonsClone[cartonIndex] = cartonUpdated;
      }

      updateCartons(cartonsClone);
    };

    const cartonSelected = cartons.find((carton) => carton.sscc === selectedCarton);
    const productsAlreadyAdded = cartonSelected?.products || [];

    const orderProductsFiltered =
      resources?.products?.filter((orderProduct) => {
        const productInOrder = orderProduct.product;

        const isFromCurrentOrder =
          !orderToFilter || orderProduct.purchaseOrder?.reference === orderToFilter;

        return (
          !productsAlreadyAdded.some(
            (productAdded) => Number(productAdded.id) === Number(productInOrder.id)
          ) &&
          (!productsAlreadyAdded?.length ||
            productsAlreadyAdded[0]?.styleColor === productInOrder?.styleColor) &&
          isFromCurrentOrder
        );
      }) || [];

    for (const availableProduct of orderProductsFiltered) {
      const cartonProducts = cartons.map((carton) => carton.products).flat(1);

      const quantityFullfilled = cartonProducts.reduce((quantity, cartonProductView) => {
        if (
          cartonProductView &&
          availableProduct?.product?.sku === cartonProductView.sku &&
          cartonProductView.poNumber === availableProduct.purchaseOrder?.reference
        ) {
          return (quantity += Number(cartonProductView.quantityInCarton || 0));
        }

        return quantity;
      }, 0);

      availableProduct.quantityFullfilled = quantityFullfilled;
    }

    return (
      <ModalAddProduct
        handleClose={() => setOpenAddProduct(false)}
        products={orderProductsFiltered || []}
        addProducts={addProducts}
        addedProductsCount={cartonSelected?.products?.length ?? 0}
        sscc={selectedCarton || ""}
        resourceCompanyId={resources?.resourceCompanyId}
        shouldShowUnitField={isBrazilDestinationPort}
        showOriginCriterion={showOriginCriterion}
      />
    );
  };

  const onSubmitCloseSSCC = () => {
    onConfirmActionInCarton(selectedId, "isClosed", true);
    closeCloseSSCCModal();
  };

  const onSubmitCloseSSCCs = () => {
    onCloseAllSSCCs();
    closeCloseSSCCsModal();
  };
  const onSubmitCloseAllSSCC = () => {
    onCloseAllSSCCs(true);
    closeCloseAllSSCCModal();
  };

  const renderAfterRow = (data?: any) => {
    const sscc = data?.sscc;

    if (!sscc) return null;

    const carton = cartons.find((carton) => carton.sscc === sscc);

    if (collapseds.find((item) => item === sscc)) return null;

    if (!carton || !carton.products || !carton.products.length) return null;

    return (
      <AfterRow
        carton={carton}
        cartons={cartons}
        updateCartons={updateCartons}
        editProductRef={editProductRef}
        isLengthMandatory={isLengthMandatory}
        viewOnly={viewOnly}
        calledFromBooking={calledFromBooking || calledFromShipment}
        notEditProduct={booking?.isBooking && !isSPPVersion}
        shouldShowUnitField={isBrazilDestinationPort}
        showOriginCriterion={showOriginCriterion}
      />
    );
  };

  const onConfirmReceiveSSCC = (allCartons: boolean, cartonSSCC?: string) => {
    if (!allCartons && !cartonSSCC) return;

    if (allCartons) {
      onConfirmActionInAllCartons("isReceivedByCfs", true);
    } else {
      onConfirmActionInCarton(cartonSSCC, "isReceivedByCfs", true);
    }
  };

  return (
    <div className={classes.mainDiv}>
      {renderModalAddProduct()}
      <UploadCSVCartons
        ref={uploadCSVRef}
        cartons={cartons}
        booking={booking}
        resources={resources}
        isLengthMandatory={isLengthMandatory}
        showOriginCriterion={showOriginCriterion}
        updateCartons={updateCartons}
        onUploadCsvS3={onUploadCsvS3}
        calledFromShipment={calledFromShipment}
        isToBrPort={isBrazilDestinationPort}
      />
      <UploadCSVReceivedSSCC
        ref={uploadCSVReceiveRef}
        updateCartons={updateCartons}
        cartons={cartons}
        bookingId={booking?.id}
      />
      <ConfirmReceiveSSCCModal ref={confirmReceiveRef} onConfirmReceive={onConfirmReceiveSSCC} />

      <div className={classes.grid}>
        <ShipeeziGrid
          data={cartonsData}
          totalItems={totalItems}
          emptyData={() => <CartonSSCCEmptySection />}
          columns={columns}
          selecteds={selecteds}
          handleSelect={setSelecteds}
          pageSize={pageSize}
          currentPage={page}
          pageSizeList={[10, 20, 40, 60, 200]}
          onChangePageSize={setPageSize}
          onNextPage={setPage}
          onPreviousPage={setPage}
          onGoToPage={setPage}
          renderAfterRow={renderAfterRow}
          keyExtractor={(carton: CartonView) => carton.sscc}
          generalActions={generalActions}
          showColumnChooser={false}
          onSorting={() => {}}
        />
      </div>

      <ModalEditProduct
        onSubmit={onSubmitEditProductFromCarton}
        ref={editProductRef}
        showLength={isLengthMandatory}
        viewOnly={viewOnly || !calledFromBooking || isFMVersion} // aqui
        resourceCompanyId={resources?.resourceCompanyId}
        shouldShowUnitOfMeasure={isBrazilDestinationPort}
        showOriginCriterion={showOriginCriterion}
      />

      <CloseSSCCModal
        onSubmit={onSubmitCloseSSCC}
        ref={closeSSCCModalRef}
        title={translate("closeSSCC")}
        text={translate("areYouSureYouWantToCloseThisSSCC")}
        warningText={translate(
          "thisActionCannotBeUndoneAndIfYouWantToModifyThisSSCCYouMustDeleteItAndCreateANewOne"
        )}
      />

      <CloseSSCCModal
        onSubmit={onSubmitCloseSSCCs}
        ref={closeSSCCsModalRef}
        title={translate("closeSSCCs")}
        text={`${translate("areYouSureYouWantToClosethese")} ${selecteds?.length || 0} ${translate(
          "ssccs"
        )}?`}
        warningText={translate(
          "thisActionCannotBeUndoneAndIfYouWantToModifyTheseSSCCsYouMustDeleteItAndCreateNewOnes"
        )}
      />

      <CloseSSCCModal
        onSubmit={onSubmitCloseAllSSCC}
        ref={closeAllSSCCModalRef}
        title={translate("closeAllSSCCs")}
        text={`${translate("areYouSureYouWantToCloseAllSSCCs")}?`}
        warningText={translate(
          "thisActionCannotBeUndoneAndIfYouWantToModifyTheseSSCCsYouMustDeleteItAndCreateNewOnes"
        )}
      />

      <ContainerModal
        ref={containerRef}
        onSubmit={AddContainersInCartons}
        containers={booking?.containers}
      />

      <CreateCommentModal ref={commentRef} onSubmit={onCreateComment} />
      <CommentsHistoryModal ref={commentsHistoryRef} onDeleteComment={onDeleteComment} />
    </div>
  );
};

export default CartonSSCC;
